import type { FormEventHandler } from 'react'
import { useEffect, useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useForm, useWatch } from 'react-hook-form'
import { differenceInCalendarDays } from 'date-fns'
import debounce from 'lodash/debounce'
import { useDoCalculate } from '@/api/kubik/credit'
import { isNonNullable } from '@/typeguards/common'
import type { TAdvancePaymentCalculatorForm } from '../../types'
import { ADVANCE_PAYMENT_CALCULATOR_FORM_SUBMIT_DEBOUNCE_TIME, DEFAULT_ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT } from './constants'

type TResult = {
  form: UseFormReturn<TAdvancePaymentCalculatorForm>
  deliveryVolume: number | null
  daysUntilHarvestDelivery: number | null
  isCalculating: boolean
  handleSubmit: FormEventHandler
}

export const useAdvancePaymentCalculatorForm = (): TResult => {
  const { mutateAsync: calculateDeliveryVolume, isLoading: isCalculating, data: deliveryVolumeResponse } = useDoCalculate()

  const form = useForm<TAdvancePaymentCalculatorForm>({
    defaultValues: {
      advanceAmount: DEFAULT_ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT,
    },
    mode: 'onTouched',
  })
  const formValues = useWatch({ control: form.control, name: ['advanceAmount', 'advancePaymentDate', 'elevator', 'productDeliveryDate'] })
  const [_advanceAmount, advancePaymentDate, _elevator, productDeliveryDate] = formValues

  const { handleSubmit: handleSubmitCalculatorForm } = form

  const handleSubmit = useMemo(
    () =>
      debounce(
        handleSubmitCalculatorForm(async values => {
          if (isNonNullable(values.advancePaymentDate) && isNonNullable(values.productDeliveryDate) && isNonNullable(values.elevator)) {
            calculateDeliveryVolume({
              advanceAmount: values.advanceAmount,
              dateOfAdvancePayment: values.advancePaymentDate.toISOString(),
              deliveryStartDate: values.productDeliveryDate.toISOString(),
              elevator: values.elevator,
            })
          }
        }),
        ADVANCE_PAYMENT_CALCULATOR_FORM_SUBMIT_DEBOUNCE_TIME,
      ),
    [calculateDeliveryVolume, handleSubmitCalculatorForm],
  )

  useEffect(() => {
    if (form.formState.isValid) {
      handleSubmit()
    }
  }, [form.formState.isValid, formValues, handleSubmit])

  const daysUntilHarvestDelivery = useMemo(() => {
    if (advancePaymentDate && productDeliveryDate) {
      return differenceInCalendarDays(productDeliveryDate, advancePaymentDate)
    }

    return null
  }, [advancePaymentDate, productDeliveryDate])

  const deliveryVolume = form.formState.isValid && deliveryVolumeResponse ? deliveryVolumeResponse.data.calculatedVolumeOfDeliveries : null

  return {
    form,
    deliveryVolume,
    daysUntilHarvestDelivery,
    isCalculating,
    handleSubmit,
  }
}
